import React, {FunctionComponent, useEffect, useRef, useState} from "react";
import {
    Grid,
    Typography,
    Box,
    Container,
    useMediaQuery,
    GridItemsAlignment,
    useTheme,
    PropTypes as MuIPropTypes,
    Card,
    Button
} from "@material-ui/core";
import {makeStyles, Theme} from "@material-ui/core/styles";

import {ReactComponent as DocSVG} from "../../Images/vetDoctor.svg";
import {BirdIconSVG, ChickenIconSVG, PetCatSVG, PetDogSVG} from "../../utils/petTypeIcons";
import {ReactComponent as LogoSVG} from "../../Images/addPetIcon.svg";
import API from "../../helpers/apiHelpers";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {setPetAge, setPetType} from "../../utils/petInfo";
import {petTypes} from "../../Constants/specialities";
import {Link, useHistory} from "react-router-dom";
import moment from "moment";
import {updatePromotionCode} from "../../redux/scheduleAppointment/scheduling.actions";
import Image from "material-ui-image";

interface Props {
    appointmentId: number;
}

declare global {
    interface String {
        toProperCase(): string;
    }
}

const useStyle = makeStyles((theme: Theme) => ({
    wrapper: {
        height: "80vh",
        marginTop: 90,
        marginBottom: 50,
        display: "flex",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            height: "auto",
        }
    },
    hrefWrapper: {
        textDecoration: "none"
    },
    headerCardWrapper: {
        paddingTop: 20,
    },
    headerInfo: {
        [theme.breakpoints.down("sm")]: {
            marginTop: 10,
            marginBottom: 10,
        }
    },
    contentWrapper: {
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "center"
        }
    },
    timeContainer: {
        display: "flex",
        flexDirection: "column",
    },
    infoCard: {
        boxShadow: "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        padding: "0 0 0 0",
        marginTop: "10px",
        borderRadius: "4px",
        width: 350,
    },
    reasonCard: {
        boxShadow: "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
        padding: "15px",
        marginTop: "20px",
        borderRadius: "4px",
    },
    img: {
        width: 130,
        height: 130,
        borderRadius: "4px 0 0 4px"
    },
    spacing: {
        paddingTop: 40,
        paddingBottom: 20
    }
}));

String.prototype.toProperCase = function () {
    return this.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};


const DoctorCard: FunctionComponent<{ appointmentId: number }> = ({appointmentId}: Props) => {
    const classes = useStyle();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const [imageLink, setImageLink] = useState("");
    const [lastName, setLastName] = useState("");
    const [education, setEducation] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState("");
    const [weight, setWeight] = useState(0);
    const [breed, setBreed] = useState("");
    const [petImage, setPetImage] = useState("");
    const [sex, setSex] = useState("");
    const [neutered, setNeutered] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");
    const history = useHistory();
    let justifyResonsiveStart: GridItemsAlignment = isMobile ? "center" : "flex-start";
    const textAlignmentAdaptiveLeft: MuIPropTypes.Alignment = isMobile ? "center" : "left";
    const schedulingReducer = useSelector((state: RootState) => state?.scheduleAppointment);
    const isWhiteLabel = useSelector((state: RootState) => state?.branding.partnerPreferencesOn);
    const logo = useSelector((state: RootState) => state?.branding.logo);
    const {immediate, serviceType, vetProfileId, pickedTimeSlot, additionalInfo} = schedulingReducer;
    const [images, setImages] = useState([]);
    const [video, setVideo] = useState(null);
    const [appointmentDetails, setAppointmentDetails] = useState("");
    const branding = useSelector((state: RootState) => state?.branding);

    useEffect(() => {
        API.getApointmentById(appointmentId)
            .then((appointment) => {
                setImages(appointment.imageUrl || []);
                if (appointment.imageUrl) {
                    setImages(appointment.imageUrl);
                } else if (Object.keys(schedulingReducer.uploadPhotos).length > 0) {
                    setImages(Object.values(schedulingReducer.uploadPhotos));
                } else {
                    setImages([]);
                }
                if (schedulingReducer?.uploadVideo) {
                    setVideo(schedulingReducer.uploadVideo);
                } else {
                    setVideo(null);
                }
                setAppointmentDetails(appointment.description || "");
                API.getAnimalById(appointment.animal.id)
                    .then((animal) => {
                        setName(animal.name || "");
                        setPetImage(animal.imageUrl || "");
                        setWeight(animal.weight || 0);
                        setBreed(animal.breed || "");
                        setSex(animal.sex.toProperCase());
                        setNeutered(animal.neuteredSpayed ? (animal.sex.toProperCase() === "Male" ? "Neutered" : "Spayed") : "Intact" || "");
                        const type = petTypes.find(type => type.value === animal.type);
                        setType(type ? `${type.label}` : "");
                        setDateOfBirth(animal.dateOfBirth || "");
                    });
                if (!immediate) {
                    API.getProfileById(vetProfileId)
                        .then((profile) => {
                            setImageLink(profile.image || schedulingReducer.vetProfile["image"]);
                            setLastName(profile.lastName || schedulingReducer.vetProfile["lastName"]);
                            setEducation(profile.education || schedulingReducer.vetProfile["education"]);
                        });
                }
            });
        dispatch(updatePromotionCode(""));
    }, []);

    return (
        <Container style={{marginTop: "3em"}}>
            <Card style={isMobile ? {padding: "40px 20px"} : {padding: 40}}>
                <Grid container justify={justifyResonsiveStart}>
                    <Grid xs={12}>
                        <Typography variant="h2" gutterBottom>Appointment Request Confirmed!</Typography>
                    </Grid>
                    <Grid xs={12}>
                        <Typography
                            variant="h4">{moment(schedulingReducer.pickedTimeSlot[0]).format("LL")} at {moment(schedulingReducer.pickedTimeSlot[0]).format("LT")}</Typography>
                    </Grid>
                    {immediate &&
                    <Grid xs={12}>
                        <Typography>A veterinary technician will contact you within 15 minutes for your
                            appointment.</Typography>
                    </Grid>
                    }
                </Grid>
                {!immediate &&
                <Grid container alignItems="center" justify="center" className={classes.spacing} spacing={2}
                    style={isMobile ? null : {marginLeft: 5}}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid container className={classes.infoCard} alignItems="center">
                                <Grid container item xs={5}>
                                    {
                                        imageLink !== "" ?
                                            <Card style={{maxWidth: 130, maxHeight: 130, borderRadius: "4px 0 0 4px"}}>
                                                <img
                                                    alt="vet image"
                                                    src={imageLink}
                                                    onError={() => {
                                                        setImageLink("");
                                                    }}
                                                    width="130"
                                                    height="130"
                                                    style={{objectFit: "cover"}}
                                                />
                                            </Card>
                                            :
                                            <DocSVG style={{maxWidth: 130, maxHeight: 130}} />
                                    }
                                </Grid>
                                <Grid container item xs={7} alignContent="center">
                                    <Box className={classes.headerInfo}>
                                        <Typography align={textAlignmentAdaptiveLeft}
                                            variant="h4">Dr. {lastName}</Typography>
                                        <Typography align={textAlignmentAdaptiveLeft}>{education}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid container className={classes.infoCard} alignItems="center">
                                <Grid container item xs={5}>
                                    {
                                        petImage !== "" ?
                                            <Card style={{maxWidth: 130, maxHeight: 130, borderRadius: "4px 0 0 4px"}}>
                                                <img
                                                    alt="pet image"
                                                    src={petImage}
                                                    onError={() => setPetImage("")}
                                                    width="130"
                                                    height="130"
                                                    style={{borderRadius: "4px 0 0 4px", objectFit: "cover"}}
                                                />
                                            </Card>
                                            :
                                            !isWhiteLabel?
                                                ({
                                                    "FELINE":
                                                    <Card style={{
                                                        maxWidth: 130,
                                                        maxHeight: 130,
                                                        borderRadius: "4px 0 0 4px"
                                                    }}>
                                                        <PetCatSVG className={classes.img}/>
                                                    </Card>,
                                                    "CANINE":
                                                    <Card style={{
                                                        maxWidth: 130,
                                                        maxHeight: 130,
                                                        borderRadius: "4px 0 0 4px"
                                                    }}>
                                                        <PetDogSVG className={classes.img}/>
                                                    </Card>,
                                                    "AVIAN":
                                                    <Card style={{
                                                        maxWidth: 130,
                                                        maxHeight: 130,
                                                        borderRadius: "4px 0 0 4px"
                                                    }}>
                                                        <BirdIconSVG className={classes.img}/>
                                                    </Card>,
                                                    "CHICKEN":
                                                    <Card style={{
                                                        maxWidth: 130,
                                                        maxHeight: 130,
                                                        borderRadius: "4px 0 0 4px"
                                                    }}>
                                                        <ChickenIconSVG className={classes.img}/>
                                                    </Card>,
                                                    "Other":
                                                    <Card style={{
                                                        maxWidth: 130,
                                                        maxHeight: 130,
                                                        borderRadius: "4px 0 0 4px"
                                                    }}>
                                                        <LogoSVG className={classes.img}/>
                                                    </Card>
                                                }[setPetType(type)])
                                                :
                                                (branding.logo
                                                &&
                                                    <Box display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        width="130px"
                                                        height="auto"
                                                        style={{
                                                            maxWidth: 130,
                                                            maxHeight: 130,
                                                            borderRadius: "4px 0 0 4px"
                                                        }}>
                                                        {branding.logo &&
                                                        <Image
                                                            alt="brand logo"
                                                            imageStyle={{objectFit: "contain"}}
                                                            src={branding.logo}
                                                            style={{width: "100%"}}/>}
                                                    </Box>)

                                    }
                                </Grid>
                                <Grid container item xs={7} alignContent="center" alignItems="center">
                                    <Box className={classes.headerInfo}>
                                        <Typography align={textAlignmentAdaptiveLeft} style={{wordBreak: "break-all"}} variant="h4">{name}</Typography>
                                        <Typography
                                            align={textAlignmentAdaptiveLeft}>{setPetAge(dateOfBirth)}</Typography>
                                        <Typography align={textAlignmentAdaptiveLeft}>{breed} {type}</Typography>
                                        {!isMobile ??
                                        <Typography
                                            align={textAlignmentAdaptiveLeft}>{weight} lbs. {neutered} {sex}</Typography>
                                        }
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                }
                <Grid container className={classes.spacing}>
                    <Grid item xs={12}>
                        <Box>
                            <Typography variant="h2">Reason for Appointment</Typography>
                        </Box>
                        <div className={classes.reasonCard}>
                            <Typography style={{whiteSpace: "pre-line"}}>{additionalInfo === "" ? appointmentDetails : additionalInfo}</Typography>
                        </div>
                    </Grid>
                    <Grid item xs={12} className={classes.spacing}>
                        <Box>
                            <Typography variant="h2">Uploaded Photos</Typography>
                        </Box>
                        <Grid container spacing={2}>
                            {images.length ? (
                                images.map(image => {
                                    return (
                                        <Grid item key={image} style={{paddingTop: 30}}>
                                            <Card style={{maxWidth: 130, maxHeight: 130, borderRadius: "4px"}}>
                                                <img
                                                    alt="image uploaded by client while booking an appointment"
                                                    src={image}
                                                    onError={() => setPetImage("")}
                                                    width="130"
                                                    height="130"
                                                    style={{borderRadius: "4px", objectFit: "cover"}}
                                                />
                                            </Card>
                                        </Grid>
                                    );
                                })
                            ) : (
                                <Grid item style={{paddingTop: 30}}>
                                    <Typography gutterBottom>No images were uploaded</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className={classes.spacing}>
                        <Box>
                            <Typography variant="h2">Uploaded Video</Typography>
                        </Box>
                        <Grid container spacing={2}>
                            {video ? (
                                <Grid item key={video} style={{paddingTop: 30}}>
                                    <Card style={{maxWidth: 130, maxHeight: 130, borderRadius: "4px"}}>
                                        <video
                                            src={video}
                                            onError={() =>
                                            {
                                                setPetImage("");
                                            }
                                            }
                                            poster={!isWhiteLabel && video.substr(4, 20).includes("video") ? require("../../../src/Images/CircleLogo.png") : logo}
                                            width="130"
                                            height="130"
                                            style={{borderRadius: "4px", objectFit: "cover"}}
                                        />
                                    </Card>
                                </Grid>
                            ) : (
                                <Grid item style={{paddingTop: 30}}>
                                    <Typography gutterBottom>No videos were uploaded</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    {!immediate &&
                    <Grid item xs={12} className={classes.spacing}>
                        <Typography variant="h2" gutterBottom>What now?</Typography>
                        <Typography gutterBottom>Your appointment is scheduled. When it is time to start your appointment, please go to the <Link className={classes.hrefWrapper}
                            to="/appointments"> Upcoming
                                Appointments </Link> tab and hit the &apos;Join Appointment&apos; button. Please reach out to <a
                            aria-label="email support at hello@thevideovets.com" href="mailto:hello@thevideovets.com">hello@thevideovets.com</a> with any
                            questions!</Typography>
                    </Grid>
                    }
                    <Grid item xs={6}>
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            style={{lineHeight: "16px"}}
                            onClick={() => {
                                history.push("/appointments");
                            }}
                        >
                            Upcoming Appointments
                        </Button>

                    </Grid>
                </Grid>
            </Card>
        </Container>
    );
};

export default DoctorCard;
